import { DetailedHTMLProps, FC, ImgHTMLAttributes } from "react";
import useImageLoadingHook from "../../hooks/useImageLoadingHook";
import SpinnerLoader from "../SpinnerLoader";
import { isBrowser } from "utils/nookies/utils";
import { classNames } from "helpers/themeHelpers";

const DefaultSpinnerImage = () => <SpinnerLoader className="centerPosition" />;

interface IImageInternalProps
  extends DetailedHTMLProps<
    ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  > {
  hasLoading?: boolean;
  fetchpriority?: string;
  CustomSpinner?: () => any;
  layout?: string;
  objectFit?: string;
  syntheticLazyload?: boolean;
  srcWebpFallback?: string;
  imageType?: string;
}

const ImageWithSynthLoading = (props) => {
  const { src, className = "", CustomSpinner, ...resetProps } = props;
  return (
    <img
      data-original-src={src}
      src="/images/grayPixel.png"
      {...resetProps}
      {...classNames(className, "imageWithSynthLoading")}
    />
  );
};

const ImageWithLoading = (props) => {
  const { src, CustomSpinner = null, ...resetProps } = props;
  const pageUrl = decodeURI(isBrowser() && window.location.href);
  const { isLoading } = useImageLoadingHook(src, pageUrl);
  if (isLoading) {
    return CustomSpinner ? CustomSpinner() : <DefaultSpinnerImage />;
  } else {
    return <img src={src} {...resetProps} />; //render fully loaded original image
  }
};

const LazyImage: FC<IImageInternalProps> = ({ src, ...props }) => {
  return <img {...props} src={src} />;
};

const ImageInternal: FC<IImageInternalProps> = (props) => {
  let {
    src,
    hasLoading,
    syntheticLazyload,
    CustomSpinner,
    srcWebpFallback,
    imageType,
    ...resetOfProps
  } = props;
  if (srcWebpFallback) {
    resetOfProps["data-src-webp-fallback"] = srcWebpFallback;
  }
  if (imageType) {
    resetOfProps["data-type"] = imageType;
  }
  if (!src?.includes("http")) {
    src = process.env.NEXT_PUBLIC_PUBLIC_FOLDER_CDN + props.src;
  }
  if (syntheticLazyload) {
    return (
      <ImageWithSynthLoading
        src={src}
        {...resetOfProps}
        CustomSpinner={CustomSpinner}
      />
    );
  }
  if (hasLoading) {
    return (
      <ImageWithLoading
        src={src}
        {...resetOfProps}
        CustomSpinner={CustomSpinner}
      />
    );
  }
  return props.loading === "lazy" ? (
    <LazyImage src={src} {...resetOfProps} />
  ) : (
    <img src={src} {...resetOfProps} />
  );
};

export default ImageInternal;
